import { DateString } from "@pentacode/openapi";
import { DateRange } from "../time";
import { getRange } from "./range.util";

export type DateParts = {
    year: string;
    month: string;
    day: string;
};

// TODO: make proper constant, user proper type
export const monthNames = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
];

export function toDateString(date: Date): DateString {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
        .getDate()
        .toString()
        .padStart(2, "0")}` as DateString;
}

export function getToday(): DateString {
    return toDateString(new Date());
}

const prevDays = new Map<DateString, DateString>();
export const getPrevDay = (date: DateString) => {
    let prev = prevDays.get(date);
    if (prev) {
        return prev;
    }
    prev = dateAdd(date, { days: -1 });
    prevDays.set(date, prev);
    return prev;
};

const nextDays = new Map<DateString, DateString>();
export const getNextDay = (date: DateString) => {
    let next = nextDays.get(date);
    if (next) {
        return next;
    }
    next = dateAdd(date, { days: 1 });
    nextDays.set(date, next);
    return next;
};

/**
 *
 * Get week number based on the [ISO week date system](https://en.wikipedia.org/wiki/ISO_week_date),
 * where each week starts with monday and the first week of the year
 * is the week that contains the first Thursday of the year.
 */
export function getWeekNumber(date: Date | DateString): number {
    const weekRange = getRange(date, "week");
    const thursday = dateAdd(weekRange.from, { days: 3 });
    const d = new Date(thursday);

    const firstDayOfYear = new Date(d.getFullYear(), 0, 1);
    const pastDaysOfYear = (d.valueOf() - firstDayOfYear.valueOf()) / 86400000;

    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

export function getDayOfWeek(date: string | Date): number {
    const d = date instanceof Date ? date : parseDateString(date);
    return d?.getDay() || 0;
}

export function parseDateString(str: string) {
    const date = new Date(str);

    // invalid date
    if (isNaN(date.getTime())) {
        return null;
    }

    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);

    return date;
}

export function getMonday(inp: DateString): DateString {
    const date = parseDateString(inp)!;
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return toDateString(date);
}

export function getSunday(inp: DateString): DateString {
    const date = parseDateString(inp)!;
    date.setDate(date.getDate() + ((7 - date.getDay()) % 7));
    return toDateString(date);
}

export function getYearRange(inp: DateString): DateRange {
    const date = parseDateString(inp)!;
    const from = new Date(date.getFullYear(), 0, 1);
    const to = new Date(date.getFullYear() + 1, 0, 1);
    return { from: toDateString(from), to: toDateString(to) };
}

export function getMonthRange(inp: DateString): DateRange {
    const date = parseDateString(inp)!;
    const from = new Date(date.getFullYear(), date.getMonth(), 1);
    const to = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    return { from: toDateString(from), to: toDateString(to) };
}

export function getWeekRange(inp: DateString): DateRange {
    const from = getMonday(inp);
    return {
        from,
        to: dateAdd(from, { days: 7 }),
    };
}

export function getDayRange(inp: DateString): DateRange {
    return { from: inp, to: dateAdd(inp, { days: 1 }) };
}

export function dateAdd(
    inp: DateString,
    { years = 0, months = 0, days = 0, weeks = 0 }: { years?: number; months?: number; days?: number; weeks?: number }
): DateString {
    const date = parseDateString(inp)!;
    if (!date) {
        throw new Error(`${inp} is not a valid date string!`);
    }

    if (weeks) {
        days += weeks * 7;
    }

    // Check number of days in the target month
    const daysInTargetMonth = new Date(date.getFullYear() + years, date.getMonth() + months + 1, 0).getDate();

    date.setFullYear(date.getFullYear() + years);
    // If target month has fewer days that current date, set the date to the maximum date in that month
    if (date.getDate() > daysInTargetMonth) {
        date.setDate(daysInTargetMonth);
    }
    date.setMonth(date.getMonth() + months);
    date.setDate(date.getDate() + days);
    return toDateString(date);
}

export function firstDayOfMonth(d?: DateString) {
    if (!d) return;

    return getMonthRange(d).from;
}

// Get difference in days
export function dateSub(start: DateString, end: DateString) {
    return Math.round((parseDateString(end)!.getTime() - parseDateString(start)!.getTime()) / 24 / 60 / 60 / 1000);
}

export function getDateArray(from: DateString, to: DateString) {
    const res: DateString[] = [];
    while (from < to) {
        res.push(from);
        from = dateAdd(from, { days: 1 });
    }
    return res;
}

export function formatDate(date: Date | string, fullYear = true) {
    const d = typeof date === "string" ? parseDateString(date)! : date;
    if (!d) {
        return "Fehlerhaftes Datum";
    }
    return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${
        fullYear ? d.getFullYear() : d.getFullYear().toString().slice(-2)
    }`;
}

export function formatDateShort(date: Date | string, numeric = false, includeYear = false) {
    const d = typeof date === "string" ? parseDateString(date)! : date;
    if (!d) {
        return "Fehlerhaftes Datum";
    }
    let str = numeric
        ? `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.`
        : `${d.getDate().toString().padStart(2, "0")}. ${monthNames[d.getMonth()].slice(0, 3)}`;
    if (includeYear) {
        str += " " + d.getFullYear().toString().slice(-2);
    }

    return str;
}

export function formatWeekDayShort(date: Date | string) {
    const d = typeof date === "string" ? parseDateString(date)! : date;
    return ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()];
}

export function formatWeekDay(date: Date | string) {
    const d = typeof date === "string" ? parseDateString(date)! : date;
    return ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][d.getDay()];
}

export function formatMonth(date: Date | string) {
    const d = typeof date === "string" ? parseDateString(date)! : date;
    return `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatMonthShort(date: Date | string, includeYear = true) {
    const d = typeof date === "string" ? parseDateString(date)! : date;
    return `${monthNames[d.getMonth()].slice(0, 3)}${includeYear ? ` ${d.getFullYear()}` : ""}`;
}

export function isValidDateString(str: string) {
    if (!str) return false;

    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(str)) return false;

    try {
        return toDateString(new Date(str)) === str;
    } catch {
        return false;
    }
}

/**
 * Get the parts of a date as separate string values (YYYY, MM, DD)
 * @param date - The date to get the parts of
 * @returns An object containing the year, month, and day as strings (e.g., {year: "2023", month: "12", day: "25"})
 */
export function getDateParts(date: Date | DateString): DateParts {
    let internalDate: Date | null = null;
    if (typeof date === "string") {
        internalDate = parseDateString(date);
    } else {
        internalDate = date;
    }

    assertDate(internalDate);

    return Intl.DateTimeFormat("default", { year: "numeric", month: "2-digit", day: "2-digit" })
        .formatToParts(internalDate)
        .reduce(
            (acc, part) => {
                acc[part.type] = part.value;
                return acc;
            },
            {} as Record<Intl.DateTimeFormatPart["type"], string>
        );
}

function assertDate(date: Date | null): asserts date is Date {
    if (!(date instanceof Date)) {
        throw new Error("Invalid date string");
    }
}
